import { GRID, MAX_TICKS, ROASTS, type Bean, type Cell, type Direction, type GameState, type InputEvent, type Roast } from './types.js';
import { nextRand, randInt } from './rng.js';

const OPPOSITE: Record<Direction, Direction> = {
  up: 'down', down: 'up', left: 'right', right: 'left',
};

const DELTA: Record<Direction, Cell> = {
  up: { x: 0, y: -1 }, down: { x: 0, y: 1 },
  left: { x: -1, y: 0 }, right: { x: 1, y: 0 },
};

function rollRoast(rngState: number): { roast: Roast; state: number } {
  const r = nextRand(rngState);
  const roast: Roast = r.value < 0.6 ? 'green' : r.value < 0.9 ? 'medium' : 'dark';
  return { roast, state: r.state };
}

/**
 * Rejection-sample a free cell, then roll the roast.
 *
 * The sampling LOOP is part of the determinism contract: every rejected draw
 * advances the RNG, so an implementation that sampled differently would
 * desynchronise from a replay even while looking correct in isolation.
 */
export function spawnBean(state: GameState): { bean: Bean; rngState: number } | null {
  // A full board has no free cell, and the rejection loop below breaks only on
  // finding one — so without this guard it spins forever. That is worse than a
  // crash: an infinite loop cannot be caught, so the server's replay would hang
  // until the function times out, leaving the run unscored and the try burnt.
  // Returning null lets `step` treat a full board as what it actually is in
  // Snake — a win.
  if (state.snake.length >= GRID * GRID) return null;

  let s = state.rngState;
  let x = 0;
  let y = 0;
  for (;;) {
    const rx = randInt(s, GRID); x = rx.value; s = rx.state;
    const ry = randInt(s, GRID); y = ry.value; s = ry.state;
    if (!state.snake.some((c) => c.x === x && c.y === y)) break;
  }
  const rolled = rollRoast(s);
  const ttl = ROASTS[rolled.roast].ttl;
  return {
    bean: { x, y, roast: rolled.roast, expiresAtTick: ttl === null ? null : state.tick + ttl },
    rngState: rolled.state,
  };
}

export function createInitialState(seed: number): GameState {
  const mid = Math.floor(GRID / 2);
  const base: GameState = {
    tick: 0,
    snake: [{ x: mid, y: mid }, { x: mid - 1, y: mid }, { x: mid - 2, y: mid }],
    dir: 'right',
    pendingGrowth: 0,
    bean: { x: 0, y: 0, roast: 'green', expiresAtTick: null },
    score: 0,
    dead: false,
    rngState: seed >>> 0,
  };
  const spawned = spawnBean(base);
  // Unreachable: a length-3 snake cannot fill a 400-cell board. Asserted rather
  // than non-null-asserted so a future GRID change surfaces loudly.
  if (!spawned) throw new Error('arcade: cannot open a run on a full board');
  return { ...base, bean: spawned.bean, rngState: spawned.rngState };
}

export function step(state: GameState, input: Direction | null): GameState {
  if (state.dead) return state;

  // A reversal is a no-op, not an error: a fat-fingered swipe must never void
  // an otherwise good run.
  const dir = input && input !== OPPOSITE[state.dir] ? input : state.dir;

  const d = DELTA[dir];
  const head: Cell = { x: state.snake[0].x + d.x, y: state.snake[0].y + d.y };
  const tick = state.tick + 1;

  if (head.x < 0 || head.y < 0 || head.x >= GRID || head.y >= GRID) {
    return { ...state, dir, tick, dead: true };
  }

  const ate = head.x === state.bean.x && head.y === state.bean.y;
  const growing = state.pendingGrowth > 0 || ate;

  // The tail vacates this tick unless we are growing, so it is not an obstacle.
  const body = growing ? state.snake : state.snake.slice(0, -1);
  if (body.some((c) => c.x === head.x && c.y === head.y)) {
    return { ...state, dir, tick, dead: true };
  }

  const snake = [head, ...body];
  // Reroll from wherever the RNG currently sits, against the post-move snake.
  const respawn = (rngState: number) => spawnBean({ ...state, snake, tick, rngState });

  // Retaining the tail this tick grants exactly ONE segment, and it can only be
  // spent once. If growth was already owed, that grant pays down the old debt —
  // so a bean eaten this tick gets no discount and must book its full growth.
  // Discounting unconditionally silently loses a segment on every overlapping
  // eat, which is ordinary play, not an edge case.
  const wasGrowing = state.pendingGrowth > 0;
  let pendingGrowth = wasGrowing ? state.pendingGrowth - 1 : 0;
  let score = state.score;
  let bean = state.bean;
  let rngState = state.rngState;

  if (ate) {
    const spec = ROASTS[state.bean.roast];
    score += spec.points;
    pendingGrowth += wasGrowing ? spec.growth : spec.growth - 1;
    const spawned = respawn(rngState);
    if (!spawned) {
      // Board full — the player has won. Ending the run here is what stops the
      // spawn loop being asked for a cell that does not exist, and the score
      // stands: a perfect game must score, not be rejected.
      return { tick, snake, dir, pendingGrowth, bean, score, dead: true, rngState };
    }
    bean = spawned.bean;
    rngState = spawned.rngState;
  } else if (bean.expiresAtTick !== null && tick >= bean.expiresAtTick) {
    // Reroll in place: a fresh bean lands the same tick, so the player never
    // starves waiting for one to come back.
    const spawned = respawn(rngState);
    if (spawned) {
      bean = spawned.bean;
      rngState = spawned.rngState;
    }
    // A null here means the board filled without an eat, which the branch above
    // already makes unreachable. Left uncrashing rather than asserted: the
    // expiry path must never be the thing that ends a legitimate run.
  }

  return { tick, snake, dir, pendingGrowth, bean, score, dead: false, rngState };
}

const DIRECTIONS: readonly string[] = ['up', 'down', 'left', 'right'];

export type ValidationResult =
  | { ok: true; inputs: InputEvent[] }
  | { ok: false; reason: string };

/**
 * Structural gate on a submitted log, run BEFORE any simulation.
 *
 * Strictly-increasing ticks matter for more than tidiness: repeated or
 * out-of-order ticks would let a submitter replay the same decision point
 * twice and search for a better outcome.
 *
 * A log is TOO LONG (rejected — `log_too_long`, still bounded by the array
 * length itself) or has an entry with a TICK past MAX_TICKS (kept, and the
 * entry silently dropped) are different things. `replay`'s own loop stops
 * advancing at `state.tick < MAX_TICKS`, so an entry whose tick sits beyond
 * that could never be consumed by a real replay — it is what a legitimate
 * long run (perimeter-hugging play routinely passes 6000 ticks) looks like
 * on the wire, not tampering. Rejecting the whole log for it turned an
 * honest long run into a burnt try and a zero score. Genuinely malformed
 * input — wrong shape, non-integer ticks, ticks that are not strictly
 * increasing, unrecognised directions, `tick < 1`, an oversized array — is
 * still rejected outright below.
 */
export function validateInputLog(inputs: unknown): ValidationResult {
  if (!Array.isArray(inputs)) return { ok: false, reason: 'malformed' };
  if (inputs.length > MAX_TICKS) return { ok: false, reason: 'log_too_long' };

  let previous = 0;
  const kept: InputEvent[] = [];
  for (const entry of inputs) {
    if (!Array.isArray(entry) || entry.length !== 2) return { ok: false, reason: 'malformed' };
    const [tick, dir] = entry as [unknown, unknown];
    if (typeof tick !== 'number' || !Number.isInteger(tick)) return { ok: false, reason: 'malformed' };
    if (typeof dir !== 'string' || !DIRECTIONS.includes(dir)) return { ok: false, reason: 'bad_direction' };
    if (tick < 1) return { ok: false, reason: 'tick_out_of_range' };
    if (tick <= previous) return { ok: false, reason: 'ticks_not_increasing' };
    previous = tick;
    // Drop rather than reject: see the function comment above. `dir` is
    // narrowed to `Direction` at runtime by the `DIRECTIONS.includes` guard
    // above, but `Array.prototype.includes` on a `readonly string[]` isn't a
    // type guard TS can see through — the cast reflects a check that already
    // happened, not a new assumption.
    if (tick <= MAX_TICKS) kept.push([tick, dir as Direction]);
  }
  return { ok: true, inputs: kept };
}

export type ReplayResult =
  | { ok: true; score: number; ticks: number }
  | { ok: false; reason: string };

/**
 * Re-simulate a run from its seed and input log, and compute the score here.
 *
 * This is the ONLY place a ranked score comes from. The client's own number is
 * never trusted — see api/arcade.ts, where it is kept purely as a tamper signal.
 */
export function replay(seed: number, inputs: InputEvent[]): ReplayResult {
  const validation = validateInputLog(inputs);
  if (!validation.ok) return { ok: false, reason: validation.reason };

  const byTick = new Map<number, Direction>(validation.inputs);
  let state = createInitialState(seed);

  while (!state.dead && state.tick < MAX_TICKS) {
    state = step(state, byTick.get(state.tick + 1) ?? null);
  }

  return { ok: true, score: state.score, ticks: state.tick };
}
